import { useEffect } from "react";
import SumsubWebSdk from "@sumsub/websdk-react";
import { Alert, Button, Container, Stack, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import api from "src/store/features/api";
import Layout from "src/components/core/Layout";
import styled from "styled-components";
import LoadingComponent from "src/components/application/LoadingComponent";
import { UKEntityOnboardingGate } from "src/components/application/uk-onboarding";

const SumsubContainer = styled.div`
  min-height: 100vh;
  background-color: #002035;

  .sumsub-websdk-container {
    height: 100%;
    display: flex;
  }

  .sumsub-websdk-container iframe {
    flex: 1;
    min-height: 500px;
  }
`;

// Mounted only once the UK entity gate has passed — entity onboarding is not
// available for UK clients, so no Sumsub applicant/token is ever created for
// them.
function EntitySumsubSdk() {
  const [generateSDKToken, { data, isLoading, isError, isUninitialized }] =
    api.useCreateAccessTokenMutation();
  const history = useHistory();

  const requestToken = () =>
    generateSDKToken({
      iCreateSumsubApplicantDto: {
        levelName: "Initial Welcome Screen",
      },
    });

  useEffect(() => {
    requestToken();
  }, []);

  if (isUninitialized || isLoading) {
    return <LoadingComponent message="Initializing Your Entity Onboarding..." />;
  }

  // Token creation failed (e.g. a stale Sumsub applicant on the account) —
  // give the user a way forward instead of a blank page.
  if (isError || !data) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Stack spacing={3}>
          <Alert severity="error">
            <Typography variant="body1" fontWeight={600} gutterBottom>
              We couldn't start your entity onboarding
            </Typography>
            <Typography variant="body2">
              Something went wrong preparing your verification session. Please try again — if the
              problem persists, contact our support team.
            </Typography>
          </Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={() => history.push("/portfolio")}
            >
              Back to Portfolio
            </Button>
            <Button variant="contained" color="primary" size="large" onClick={requestToken}>
              Try Again
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <SumsubContainer>
      <SumsubWebSdk
        accessToken={data.token}
        className="sumsub-websdk-container"
        expirationHandler={() =>
          generateSDKToken({
            iCreateSumsubApplicantDto: {
              levelName: "Initial Welcome Screen",
            },
          })
            .unwrap()
            .then((res) => res.token)
        }
        onMessage={(event) => {
          console.log("Sumsub message: ", event);
        }}
      />
    </SumsubContainer>
  );
}

export default function Sumsub() {
  return (
    <Layout>
      <UKEntityOnboardingGate>
        <EntitySumsubSdk />
      </UKEntityOnboardingGate>
    </Layout>
  );
}
